// Rules.js
import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, ButtonGroup, Card, CardContent, CardHeader, Grid, IconButton, Paper, Popover, Slider, TextField, Tooltip, } from '@mui/material';
import { styled } from '@mui/material/styles';
import HelpIcon from '@mui/icons-material/Help';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import RestartAltIcon from '@mui/icons-material/RestartAlt'; // reset to default rule
import PlayArrowIcon from '@mui/icons-material/PlayArrow'; // start simulation
import { AppStyle, DefaultRule } from '../value/AppConst';
import { ruleToApplyAtom, simulationRuleAtom, stockDataAtom } from '../value/AppModel';
import { useAtom, useSetAtom } from 'jotai';
import { Plain, SectionBody, SectionTitle } from './StyledViews';
import { useSnackbar } from 'notistack';
import MiscValue from '../value/MiscValue';
import AppUtil from '../util/AppUtil';

const HGrid = styled(Grid)({paddingTop:12, paddingBottom:4});
const LG = styled(HGrid)({fontSize: '0.85rem'});


export default function Rules() {
    const [simulationRule, setSimulationRule] = useAtom(simulationRuleAtom);
    const [ruleChanged, setRuleChanged] = useState(false);
    const [stockRecords] = useAtom(stockDataAtom);  // rollingHigh 설정해야 함.
    const setRuleToApply = useSetAtom(ruleToApplyAtom);
    const [dates, setDates] = useState([]);
    const [dateRange, setDateRange] = useState([0, 0]);
    const [errorCount, setErrorCount] = useState(0);
    const [anchors, setAnchors] = useState({});
    const { enqueueSnackbar } = useSnackbar();
    const [explain, setExplain] = useState(false);

    useEffect(() => {
        const errCnt = stockRecords.reduce((count, rec) => rec.error ? count + 1 : count, 0);
        const dateList = stockRecords.map(rec => rec.date);
        setDates(dateList);
        setDateRange([0, dateList.length - 1]);
        setErrorCount(errCnt);
    }, [stockRecords]);

    const onClickHelp = (field, event) => {
        setAnchors({[field]: event.currentTarget});
    };

    const onChangeInteger = (field, value) => {
        setSimulationRule(prev => ({
            ...prev,
            [field]: Math.min(Math.max(value, DefaultRule[field].min), DefaultRule[field].max)
        }));
        setRuleChanged(true);
    };

    const addBuyAmount = (diff) => {
        let prev = {...simulationRule}.buyAmount;
        if(!prev) prev = 0;
        let newAmount = Math.min(Math.max(prev + diff, DefaultRule.buyAmount.min), DefaultRule.buyAmount.max);

        onChangeInteger('buyAmount', newAmount);
    }

    const onChangePercent = (field, value) => {
        // partialSellOnProfit, exitOnProfit 관계 점검.
        if(field==='exitOnProfit') {
            if(value <= simulationRule.partialSellOnProfit * 100) {
                //enqueueSnackbar('부분 매도 실행 수익률보다 커야 합니다.', { variant: 'error', autoHideDuration: 1500 });
                //return;
                value = simulationRule.partialSellOnProfit * 100 + 1; // 강제 조정
            }
        }
        if(field==='partialSellOnProfit') {
            if(value >= simulationRule.exitOnProfit * 100) {
                // enqueueSnackbar('전체 매도 수익률보다 작아야 합니다.', { variant: 'error', autoHideDuration: 1500 });
                // return;
                value = simulationRule.exitOnProfit * 100 - 1; // 강제 조정
            }
        }
        setSimulationRule(prev => ({
            ...prev,
            [field]: Math.min(Math.max(value / 100, DefaultRule[field].min), DefaultRule[field].max)
        }));
        setRuleChanged(true);
    };

    const onClickStartSimulation = () => {
        const ruleToApply = {...simulationRule};
        ruleToApply.startDate = dates[dateRange[0]];
        ruleToApply.endDate = dates[dateRange[1]];
        setRuleToApply(ruleToApply);
        AppUtil.saveRuleInLocalStorage(simulationRule);
    };

    const renderIntSlider = (field, label) => {
        return (
            <>
            <LG size={4}>{label}
                {!explain &&
                <IconButton size="small" onClick={(e) => onClickHelp(field, e)} style={{marginLeft: 5}}>
                    <HelpIcon />
                </IconButton>}
                <Popover
                    open={!!anchors[field]}
                    anchorEl={anchors[field]}
                    onClose={() => setAnchors({})}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Paper sx={{ padding: 2, maxWidth: 300 }}>{DefaultRule[field].help}</Paper>
                </Popover>
            </LG>
            <HGrid size={8}>
                {
                    explain ? DefaultRule[field].help :
                    <Tooltip title={DefaultRule[field].help}>
                        <Slider
                            value={simulationRule[field] || DefaultRule[field].default}
                            onChange={(e, value) => onChangeInteger(field, value)}
                            min={DefaultRule[field].min}
                            max={DefaultRule[field].max}
                            valueLabelDisplay='on'
                        />
                    </Tooltip>
                }
            </HGrid>
            </>
        )
    };

    const renderPercentSlider = (field, label, step) => {
        return (
            <>
                <LG size={4}>{label}
                    {!explain &&
                    <IconButton size="small" onClick={(e) => onClickHelp(field, e)} style={{marginLeft: 5}}>
                        <HelpIcon />
                    </IconButton>}
                    <Popover
                        open={!!anchors[field]}
                        anchorEl={anchors[field]}
                        onClose={() => setAnchors({})}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'left',
                        }}
                    >
                        <Paper sx={{ padding: 2, maxWidth: 300 }}>{DefaultRule[field].help}</Paper>
                    </Popover>
                </LG>
                <HGrid size={8}>
                    {
                        explain ? DefaultRule[field].help :
                        <Tooltip title={DefaultRule[field].help}>
                        <Slider
                            value={simulationRule[field]===undefined ? DefaultRule[field].default*100 : simulationRule[field]*100}
                            valueLabelFormat={(value) => `${value}%`}
                            onChange={(e, value) => onChangePercent(field, value)}
                            min={DefaultRule[field].min*100}
                            max={DefaultRule[field].max*100}
                            valueLabelDisplay='on'
                            step={step || 1}
                        />
                        </Tooltip>
                    }
                </HGrid>
            </>
        )
    };

    const renderBuyAmount = () => {
        const field = 'buyAmount';
        return(
            <>
            <LG size={4}>C. 1회 기본 매수 금액 ({DefaultRule.buyAmount.min} ~ {MiscValue.formatInt(DefaultRule.buyAmount.max)})
                {!explain &&
                <IconButton size="small" onClick={(e) => onClickHelp(field, e)} style={{marginLeft: 5}}>
                    <HelpIcon />
                </IconButton>}
                <Popover
                    open={!!anchors[field]}
                    anchorEl={anchors[field]}
                    onClose={() => setAnchors({})}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                >
                    <Paper sx={{ padding: 2, maxWidth: 300 }}>{DefaultRule[field].help}</Paper>
                </Popover>
            </LG>
            <HGrid size={8} style={{display: 'flex', alignItems: 'center'}}>
                {
                    explain ? DefaultRule[field].help :
                    <Tooltip title={DefaultRule[field].help}>
                        <TextField
                            type="number"
                            value={simulationRule.buyAmount || DefaultRule.buyAmount.default}
                            onChange={(e) => onChangeInteger('buyAmount', e.target.value)}
                            style={{marginRight: 24, maxWidth: 160}}
                            slotProps={{htmlInput: { step: 10000 }}}
                        />
                    </Tooltip>
                }
                {
                    simulationRule.buyAmount && !explain &&
                    <span style={{color: '#77f'}}>
                        {MiscValue.formatFloat(simulationRule.buyAmount)}원
                    </span>
                }
                {
                    !explain &&
                    <Box style={{flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end'}}>
                        <Box>
                            <div><Button size="small" variant='outlined' onClick={() => addBuyAmount(10000)}>+1만</Button></div>
                            <div><Button size="small" variant='outlined' onClick={() => addBuyAmount(-10000)}>-1만</Button></div>
                        </Box>
                        <Box>
                            <div><Button size="small" variant='outlined' onClick={() => addBuyAmount(50000)}>+5만</Button></div>
                            <div><Button size="small" variant='outlined' onClick={() => addBuyAmount(-50000)}>-5만</Button></div>
                        </Box>
                        <Box>
                            <div><Button size="small" variant='outlined' onClick={() => addBuyAmount(100000)}>+10만</Button></div>
                            <div><Button size="small" variant='outlined' onClick={() => addBuyAmount(-100000)}>-10만</Button></div>
                        </Box>
                        <Box>
                            <div><Button size="small" variant='outlined' onClick={() => addBuyAmount(500000)}>+50만</Button></div>
                            <div><Button size="small" variant='outlined' onClick={() => addBuyAmount(-500000)}>-50만</Button></div>
                        </Box>
                    </Box>
                }
            </HGrid>
            </>
        )
    };

    const renderStory = () => {
        if(errorCount > 0 || stockRecords.length < simulationRule.buyInterval * simulationRule.minimalTradeTimes / 2) return null;
        return(
            <Card style={{margin: 16, backgroundColor: '#eef'}}>
                <CardHeader title="매매규칙 설정에 따른 시뮬레이션 시나리오" />
                <CardContent style={{fontSize: '0.95rem', lineHeight: 1.5}}>
                    <span>이 시뮬레이션을 실행하면 주어진 기간 {dates[dateRange[0]]}일 부터 {dates[dateRange[1]]}일 까지 계속 </span>
                    <span>
                        {simulationRule.buyInterval}일 간격으로 기본 {MiscValue.formatFloat(simulationRule.buyAmount)}원어치의 주식을 매수하는 시뮬레이션을 진행합니다. 매수나 매도는 시가와 종가의 중간값을 사용합니다. 
                    </span><br />
                    <span>만일, 어느 날의 시가가 평균단가의 {simulationRule.exitOnProfit*100}% 이상 상승하면 전량 매도합니다. </span>
                    <span>단, 전량매도는 "최소 거래 횟수" {simulationRule.minimalTradeTimes}회 이상 진행된 이후에만 가능하며, 전량매도 후에는 시뮬레이션은 종료됩니다. </span>
                    <br/>
                    {
                        simulationRule.partialSellThreshold > 0 &&
                        <span>전량 매도 이전이라도 시가가 평균단가의 {simulationRule.partialSellOnProfit*100}% 이상 상승하면 보유 물량의 {simulationRule.partialSellThreshold * 100}%를 매도합니다. "J. 부분 매도 시 매도비중"이 0%이면 부분매도는 일어나지 않습니다.<br/></span>
                    }
                    <span>평균단가 대비 {simulationRule.acceptableChange*100}%(무시할 수 있는 상승/하락폭)를 초과하여 상승하거나 하락하면 매수 간격을 조절할 수도 있습니다. 현재는 </span>
                    {
                        simulationRule.bullIntervalFactor === 1
                        ?
                        <span>{simulationRule.acceptableChange*100}% 초과 상승해도 매수 간격을 조절하지 않도록 설정 되었습니다. </span>
                        :
                        simulationRule.bullIntervalFactor < 1
                        ?
                        <span>{simulationRule.acceptableChange*100}% 초과 상승하면 매수 간격이 {Math.round(simulationRule.buyInterval * simulationRule.bullIntervalFactor)}일로 짧아집니다. </span>
                        :
                        <span>{simulationRule.acceptableChange*100}% 초과 상승하면 매수 간격이 {Math.round(simulationRule.buyInterval * simulationRule.bullIntervalFactor)}일로 늘어납니다. </span>
                    } <span> 또, {simulationRule.acceptableChange*100}% 초과 </span>
                    {
                        simulationRule.bearIntervalFactor === 1
                        ?
                        <span>하락해도 매수 간격을 조절하지 않도록 설정 되었습니다. </span>
                        :
                        simulationRule.bearIntervalFactor < 1
                        ?
                        <span>하락하면 매수 간격이 {Math.round(simulationRule.buyInterval * simulationRule.bearIntervalFactor)}일로 짧아집니다. </span>
                        :
                        <span>하락하면 매수 간격이 {Math.round(simulationRule.buyInterval * simulationRule.bearIntervalFactor)}일로 늘어납니다. </span>
                    }<br />
                    <span>손실을 보고 있을 때, 손실률에 비례해서 매수 금액을 늘릴 수도 있습니다. </span>
                    {
                        simulationRule.avgDownFactorMax > 1
                        ?
                        <span>현재는 손실일 경우 1회 기본 매수 금액의 최대 {simulationRule.avgDownFactorMax * 100}%까지 매수 금액이 늘어날 수 있습니다.</span>
                        :
                        <span>현재는 손실이 난 상태라도 매수 금액을 늘리지 않도록 설정되어 있습니다.</span>
                    }<br/>
                    <span>
                        일정기간내 일정비율로 주가가 급락하는 경우 추가적으로 매수를 실행할 수도 있습니다. 급락매수 시 매수금액 비율을 0이 아닌 값으로 설정하면 다른 설정값들을 함께 고려하여 급락매수를 실행합니다. 현재는 
                    </span>
                    {
                        simulationRule.suddenDropBuyFactor > 0
                        ?
                        <span> 급락기준 롤링 최고가 기간인 {simulationRule.rollingHighPeriod}일 이전부터 당일 이전까지의 종가 최고가로부터 당일 시가가 급락률 {simulationRule.suddenDropBuyThreshold * 100}% 이상 하락하면 1회 기본 매수 금액의 {
                            simulationRule.suddenDropBuyFactor * 100}%인 {MiscValue.formatFloat(Math.round(simulationRule.buyAmount * simulationRule.suddenDropBuyFactor))}원 만큼 추가로 매수합니다.
                        </span>
                        :
                        <span> 급락매수를 실행하지 않도록 설정되어 있습니다.</span>
                    }
                    <p>설정값을 적절히 변경하면서 수차례 시뮬레이션을 실행해 보고 자신에게 맞는 최적의 분할매수 전략을 찾아보세요.</p>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box>
            <SectionTitle label="매매 규칙" disabled={stockRecords.length < 10} />
            <SectionBody style={{
                padding: 16, border: '1px solid ' + AppStyle.PrimaryRGB, borderRadius: 4, marginTop: 8}}>
                <Grid container spacing={2}>
                    <Grid size={4}></Grid>
                    <Grid size={8} style={{textAlign:'right'}}>
                        <ButtonGroup variant='contained'>
                            <Button
                                disabled={explain}
                                onClick={()=>setExplain(true)}
                                startIcon={<InfoIcon />}
                                color={AppStyle.mui.success}
                            >
                                설명 보기
                            </Button>
                            <Button
                                disabled={!explain}
                                onClick={()=>setExplain(false)}
                                startIcon={<EditIcon />}
                                color={AppStyle.mui.secondary}
                            >
                                설정하기
                            </Button>
                        </ButtonGroup>
                    </Grid>
                    {renderIntSlider('minimalTradeTimes', 'A. 최소 거래 횟수')}
                    {renderIntSlider('buyInterval', 'B. 기본 매수 간격(일)')}
                    {renderBuyAmount()}
                    {renderPercentSlider('bullIntervalFactor', 'D. 상승 시 매수간격 조절(%)')}
                    {renderPercentSlider('bearIntervalFactor', 'E. 하락 시 매수간격 조절(%)')}
                    {renderPercentSlider('acceptableChange', 'F. 무시할 수 있는 상승/하락폭(%)', 1)}
                    {renderPercentSlider('avgDownFactorMax', 'G. 손실 시 매수금액 증액(%)', 5)}
                    {renderPercentSlider('partialSellOnProfit', 'H. 부분 매도를 실행할 수익률(%)', 1)}
                    {renderPercentSlider('partialSellThreshold', 'J. 부분 매도 시 매도비중(%)', 1)}
                    {renderPercentSlider('exitOnProfit', 'K. 전체 매도할 수익률(%)', 1)}
                    {/* {renderPercentSlider('exitOnProfitDrop', 'L. K점 이후 실제 매도할 하락률(%)', 1)} */}
                    {renderPercentSlider('suddenDropBuyThreshold', 'L. 급락률(얼마나 급락하면 매수할지 결정)', 1)}
                    {renderPercentSlider('suddenDropBuyFactor', 'M. 급락매수 시 매수금액 비율(%)', 1)}
                    {renderIntSlider('rollingHighPeriod', 'N. 급락기준 롤링 최고가 기간(일)', 1)}
                    <HGrid size={12}>
                        투자 시뮬레이션 범위:
                        <span style={{fontSize: '1.1rem', marginLeft:10, color: AppStyle.SecondaryRGB}}>
                            {dates[dateRange[0]]} - {dates[dateRange[1]]}
                        </span>
                    </HGrid>
                    <Grid size={1}></Grid>
                    <Grid size={10} style={{marginTop: 8}}>
                        <Slider
                            value={dateRange}
                            onChange={(e, newValue) => setDateRange(newValue)}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => dates[value]}
                            min={0}
                            max={dates.length - 1}
                            marks={[
                                { value: 0, label: dates[0] },
                                { value: dates.length - 1, label: dates[dates.length - 1] },
                            ]}
                        />
                    </Grid>
                    <Grid size={1}></Grid>
                </Grid>
                {renderStory()}
                <Grid container spacing={4} style={{marginTop: 8}}>
                    <Grid size={6}>
                        <Button
                            onClick={() => {
                                setSimulationRule(
                                    Object.fromEntries(
                                        Object.entries(DefaultRule).map(([key, value]) => [key, value.default])
                                    )
                                );
                            }}
                            variant='contained' size='large'
                            color={AppStyle.mui.success}
                            fullWidth
                            startIcon={<RestartAltIcon />}
                        >
                            기본 설정으로 초기화
                        </Button>
                    </Grid>
                    <Grid size={6}>
                        <Button
                            disabled={errorCount > 0 || stockRecords.length < simulationRule.buyInterval * simulationRule.minimalTradeTimes / 2}
                            onClick={onClickStartSimulation}
                            variant='contained' size='large'
                            fullWidth
                            startIcon={<PlayArrowIcon />}
                        >
                            {
                                errorCount > 0
                                ? '오류가 있으므로 시뮬레이션을 시작할 수 없습니다.'
                                : stockRecords.length < 10
                                ? '시뮬레이션을 실행할 만한 데이터가 부족합니다.'
                                : '시뮬레이션을 시작합니다.'
                            }
                        </Button>
                    </Grid>
                </Grid>
            </SectionBody>
        </Box>
    );
}

/*

[섹션 3: 매매 규칙] Rules.js
    - 최소 거래 횟수. 이 횟수만큼 거래하지 않았다면 팔지 않음 (minimalTradeTimes, 최소값=1, 최대값=10)
    - 기본 매수 간격 (일 단위) (buyInterval, 최소값=3, 최대값=40)
    - 기본 매수 금액 (원 단위) (buyAmount, 최소값=10, 최대값=10,000,000)
    - 상승 시 매수간격 조절 배율 (예: 0.8) (bullIntervalFactor, 최소값=0.3, 최대값=2.0)
    - 하락 시 매수간격 조절 배율 (예: 1.5) (bearIntervalFactor, 최소값=0.3, 최대값=2.0)
    - 손실을 보고 있을 때 매수금액 증액 비율 최대값 (1.5배) (avgDownFactorMax, 최소값=1.0, 최대값=3.0) : (1.5-1) * 손실비율. ex: (1.5-1) * 0.2 = 0.1 => 1.1배 매수.
    - 무시할 수 있는 상승 또는 하락폭 (예: 5%) (acceptableChange, 최소값=0.02, 최대값=1.0)
    - 전체 매도할 수익률 (예: 30%) (exitOnProfit, 최소값=0.1, 최대값=3.0)
    - 부분 매도 시작 수익률 (예: 20%) (partialSellOnProfit, 최소값=0.1, 최대값=3.0)
    - 부분 매도 비중 (예: 25%) (partialSellThreshold, 최소값=0.1, 최대값=0.5)
    - 급격한 하락에 대한 매수 기준 (15% 이내) (suddenDropBuyThreshold, 최소값=0.1, 최대값=0.5)
    - 급격한 하락 시 매수 금액 조절 (0.2배) (suddenDropBuyFactor, 최소값=0, 최대값=1.0) 이 기간이 코비드 기간처럼 장기화 될 수 있으므로 1보다 크지 말아야 한다. 값이 0이면 급격한 하락에 대한 매수는 하지 않음.
    - 롤링 최고가 기간 (5일) (rollingHighPeriod, 최소값=1, 최대값=30) $suddenDropBuyThreshold 적용의 기준이 되는 값. 현재 가격이 $rollingHighPeriod 기간의 최고값 대비 $suddenDropBuyThreshold 만큼 떨어져 있는 경우 매수한다. 또한 급락매수한 경우 이 값과 동일한 기간 내에 다시 매수하지는 않는다.
    # 수익이 partialSellOnProfit 퍼센트 이상인 경우 보유주식수의 partialSellThreshold 퍼센트 매도하고 매수하지 않음.
    - 버튼: "가상 매매 시뮬레이션 시작" 버튼을 누르면 설정된 규칙에 따라 매매 시뮬레이션을 실행하여 결과 값을 만들어 냄.
    - 로컬스토리지: 규칙 정보는 로컬 스토리지에 저장함.
*/
